/* ============================================================================
   CircleDrops — "Just For You" section externalized script
   ----------------------------------------------------------------------------
   Save this file as:  assets/section-hp-just-for-you.js
   Loaded from section-hp-just-for-you.liquid with:
     <script src="{{ 'section-hp-just-for-you.js' | asset_url }}" defer></script>

   Shopify does NOT render Liquid inside asset .js files, so the three Liquid
   touchpoints the inline script used are passed in via data-* attributes on the
   section container (rendered by Liquid):

     <section ... data-jfy-container
                  data-uid="{{ uid }}"
                  data-customer-id="{% if customer %}{{ customer.id }}{% endif %}"
                  ... >

   Mapping from the old inline Liquid:
     {{ uid }}                               -> CONTAINER.dataset.uid  (SECTION_ID)
     {% if customer %}{{ customer.id }}{% endif %} -> CONTAINER.dataset.customerId (CUSTOMER_ID)
     window.jfyArrowUpdate_{{ uid | replace: '-', '_' }}
                                             -> computed at runtime from SECTION_ID

   The <style> block stays inline in the .liquid (it is Liquid-driven CSS, not
   logic). The product FETCHING mechanism (Supabase -> fallback Shopify
   collection -> Section Rendering API card injection) is UNTOUCHED.

   This file initialises EVERY [data-jfy-container] on the page, so the section
   still works if placed more than once. Each container is guarded against
   double-init.
   ========================================================================== */

(function () {
  'use strict';

  function initJFY(CONTAINER) {
    if (!CONTAINER || CONTAINER.__jfyInit) return;
    CONTAINER.__jfyInit = true;

    const SECTION_ID = CONTAINER.dataset.uid || (CONTAINER.id || '').replace(/^JustForYou-/, '');

    const LIST = CONTAINER.querySelector('[data-jfy-list]');
    const EMPTY = CONTAINER.querySelector('[data-jfy-empty]');
    const LOADING = CONTAINER.querySelector('[data-jfy-loading]');
    const PREV = CONTAINER.querySelector('[data-jfy-prev]');
    const NEXT = CONTAINER.querySelector('[data-jfy-next]');
    const LIMIT = parseInt(CONTAINER.dataset.limit, 10) || 12;
    const FALLBACK_HANDLE = CONTAINER.dataset.fallbackHandle || 'just-for-you';

    const ITEMS_DESKTOP = parseFloat(CONTAINER.dataset.itemsDesktop) || 4;
    const ITEMS_MOBILE = parseFloat(CONTAINER.dataset.itemsMobile) || 2.2;
    const MIRROR_THEME = CONTAINER.dataset.mirror === '1';
    const IS_SLIDER = LIST && LIST.classList.contains('slider');

    // Logged-in customer ID (injected server-side by Liquid via data-customer-id) — priority identifier
    const CUSTOMER_ID = (CONTAINER.dataset.customerId || '').trim() || null;

    // Supabase configuration — hardcoded defaults so the section works out of the box.
    // Schema settings will override these if filled in.
    const DEFAULT_SUPABASE_URL = 'https://yvboydafmpdlrhcukcuc.supabase.co/';
    const DEFAULT_SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inl2Ym95ZGFmbXBkbHJoY3VrY3VjIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTk5NzQ3MTcsImV4cCI6MjA3NTU1MDcxN30.wTgmPGVCHl4dSlTHb1S9L4cSvMi1GU2BA_xf33L3tqM';
    const SUPABASE_URL = ((CONTAINER.dataset.supabaseUrl || '').trim() || DEFAULT_SUPABASE_URL).replace(/\/+$/, '');
    const SUPABASE_ANON_KEY = (CONTAINER.dataset.supabaseKey || '').trim() || DEFAULT_SUPABASE_ANON_KEY;
    const SUPABASE_TABLE = CONTAINER.dataset.supabaseTable || 'User Recommendations';

    console.info('[JFY] Supabase URL:', SUPABASE_URL);
    console.info('[JFY] Supabase key length:', SUPABASE_ANON_KEY.length);
    console.info('[JFY] Customer ID from Liquid:', CUSTOMER_ID);

    const CACHE_KEY = 'jfy_recommendations_v5';
    const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

    // Allow force-refresh via ?jfy_fresh=1 in the URL
    const FORCE_FRESH = new URLSearchParams(window.location.search).has('jfy_fresh');
    if (FORCE_FRESH) {
      try { sessionStorage.removeItem(CACHE_KEY); } catch (e) {}
    }

    function setArrowsHidden(hidden) {
      if (PREV) PREV.hidden = hidden;
      if (NEXT) NEXT.hidden = hidden;
    }

    // The arrows are spans, so there is no `disabled` property. We mirror it
    // with a class + aria-disabled. Note we deliberately do NOT add the theme's
    // `swiper-button-disabled` class: screen.css hides that outright
    // (#root .swiper-button-disabled { display: none }) whereas New Arrivals
    // keeps both arrows visible at the ends — so we match New Arrivals and just
    // make the end arrow inert.
    function setDisabled(el, isDisabled) {
      if (!el) return;
      el.classList.toggle('is-disabled', !!isDisabled);
      el.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
    }

    function showLoading() {
      if (LOADING) LOADING.hidden = false;
      if (EMPTY) EMPTY.hidden = true;
      if (LIST) LIST.style.display = 'none';
      setArrowsHidden(true);
    }
    function hideLoading() { if (LOADING) LOADING.hidden = true; }
    function showEmpty() {
      hideLoading();
      if (LIST) LIST.style.display = 'none';
      setArrowsHidden(true);
      if (EMPTY) EMPTY.hidden = false;
    }
    function showList() {
      hideLoading();
      if (EMPTY) EMPTY.hidden = true;
      if (LIST) LIST.style.display = '';
    }

    // Get identifier — logged-in customer.id takes priority, otherwise falls back to GA4 client ID
    function getIdentifier() {
      if (CUSTOMER_ID) {
        console.info('[JFY] Using customer.id as identifier:', CUSTOMER_ID);
        return String(CUSTOMER_ID);
      }
      const gaId = getGA4ClientId();
      console.info('[JFY] Using GA4 client ID as identifier:', gaId);
      return gaId;
    }

    // GA4 client ID resolution — 5-tier fallback (mirrors original custom liquid)
    function getGA4ClientId() {
      // 1) Global set by GA4
      if (window.ga4ClientId) return String(window.ga4ClientId);

      // 2) _ga cookie: format "GA1.X.<clientId>" — take everything after "GA1.X."
      try {
        const gaCookie = document.cookie.split('; ').find(function(c) { return c.startsWith('_ga='); });
        if (gaCookie) {
          const value = gaCookie.split('=')[1];
          const parts = value.split('.');
          if (parts.length >= 4) {
            return parts.slice(2).join('.');
          }
        }
      } catch (e) {}

      // 3) _ga_G-XXXX cookie (GA4 property cookie)
      try {
        const ga4Cookie = document.cookie.split('; ').find(function(c) { return c.startsWith('_ga_G-') || c.startsWith('_ga_'); });
        if (ga4Cookie) {
          const value = ga4Cookie.split('=')[1];
          const parts = value.split('.');
          if (parts.length >= 3) {
            return parts[2];
          }
        }
      } catch (e) {}

      // 4) localStorage
      try {
        const stored = localStorage.getItem('ga4_client_id');
        if (stored) return stored;
      } catch (e) {}

      // 5) Random fallback
      const random = 'random-' + Math.random().toString(36).substring(2, 15) + '-' + Date.now();
      try { localStorage.setItem('ga4_client_id', random); } catch (e) {}
      return random;
    }

    // Cache helpers
    function getCache() {
      try {
        const raw = sessionStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (Date.now() - parsed.ts > CACHE_TTL_MS) return null;
        return parsed.handles;
      } catch (e) { return null; }
    }
    function setCache(handles) {
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), handles: handles }));
      } catch (e) {}
    }

    // Fetch handles from Supabase (priority order)
    async function fetchSupabaseHandles() {
      if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
        console.info('[JFY] Skipping Supabase — URL or key not configured.');
        return [];
      }
      try {
        const clientId = getIdentifier();
        const url = SUPABASE_URL + '/rest/v1/User%20Recommendations?user_id=eq.' + encodeURIComponent(clientId) + '&select=recommended_handles&limit=1';
        console.info('[JFY] Fetching Supabase for client_id:', clientId);
        const resp = await fetch(url, {
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': 'Bearer ' + SUPABASE_ANON_KEY,
            'Content-Type': 'application/json',
            'Accept-Profile': 'public'
          }
        });
        console.info('[JFY] Supabase response status:', resp.status);
        if (resp.ok) {
          const data = await resp.json();
          console.info('[JFY] Supabase response data:', data);
          if (data && data[0]) {
            let handles = data[0].recommended_handles;

            // Supabase sometimes stores this as a JSON string instead of an array — parse if needed.
            if (typeof handles === 'string') {
              try {
                handles = JSON.parse(handles);
                console.info('[JFY] Parsed recommended_handles from JSON string');
              } catch (e) {
                console.warn('[JFY] Failed to parse recommended_handles string:', e);
                handles = null;
              }
            }

            if (Array.isArray(handles) && handles.length > 0) {
              console.info('[JFY] Supabase returned', handles.length, 'handles');
              return handles;
            }
          }
          console.info('[JFY] Supabase returned no recommendations for this identifier.');
        }
      } catch (e) {
        console.warn('[JFY] Supabase fetch failed:', e);
      }
      return [];
    }

    // Fetch handles from the fallback Shopify collection
    async function fetchFallbackHandles() {
      try {
        const url = '/collections/' + FALLBACK_HANDLE + '/products.json?limit=' + Math.max(LIMIT * 2, 20);
        console.info('[JFY] Fetching fallback collection:', url);
        const resp = await fetch(url);
        if (resp.ok) {
          const data = await resp.json();
          if (data.products && data.products.length) {
            const handles = data.products
              .filter(function(p) { return p.available !== false; })
              .map(function(p) { return p.handle; });
            console.info('[JFY] Fallback returned', handles.length, 'available handles');
            return handles;
          }
        }
      } catch (e) {
        console.warn('[JFY] Fallback collection fetch failed:', e);
      }
      return [];
    }

    // Either/or: use Supabase exclusively when it has data, fall back to Shopify collection only when Supabase is empty.
    async function fetchHandles() {
      const cached = getCache();
      if (cached && cached.length) {
        console.info('[JFY] Using cached handles (add ?jfy_fresh=1 to URL to bypass):', cached);
        return cached;
      }

      // 1) Try Supabase first
      const supabaseHandles = await fetchSupabaseHandles();
      if (supabaseHandles && supabaseHandles.length > 0) {
        const handles = supabaseHandles.slice(0, LIMIT);
        console.info('[JFY] Rendering from Supabase (' + handles.length + ' handles):', handles);
        setCache(handles);
        return handles;
      }

      // 2) Only if Supabase returned nothing, use the fallback Shopify collection
      console.info('[JFY] Supabase empty — falling back to Shopify collection:', FALLBACK_HANDLE);
      const fallbackHandles = await fetchFallbackHandles();
      const handles = fallbackHandles.slice(0, LIMIT);
      console.info('[JFY] Rendering from fallback collection (' + handles.length + ' handles):', handles);
      // Don't cache fallback-only — we want to re-check Supabase on next load in case new recommendations arrived.
      return handles;
    }

    /* ============================================================
       LAYOUT PARITY WITH THE NEW ARRIVALS SLIDER
       ------------------------------------------------------------
       The theme's slider JS ran before our cards existed, so we
       replicate the result. We find a "donor" — any other
       .l4cl product list already rendered on this page (New
       Arrivals) — and copy its gap, scroll-snap and real card
       width. That guarantees identical card size and spacing on
       both desktop and mobile. If there is no donor, we compute
       the same layout from the schema settings instead.
       ============================================================ */

    function isDesktop() {
      return window.matchMedia('(min-width: 47.5em)').matches;
    }

    function readGap(el) {
      if (!el) return null;
      const cs = getComputedStyle(el);
      const g = parseFloat(cs.columnGap);
      if (!isNaN(g)) return g;
      const g2 = parseFloat(cs.gap);
      return isNaN(g2) ? null : g2;
    }

    function applyLayout() {
      if (!LIST || !IS_SLIDER) return;

      const items = Array.from(LIST.children);
      if (!items.length) return;

      if (MIRROR_THEME) {
        /* ------------------------------------------------------------
           Let the THEME size the cards. Our <li>s carry the same
           l4cl + wNN classes as New Arrivals, so screen.css already
           gives them the correct width and gutter:

             desktop : .l4cl.w25 li { width: 25% }
             mobile  : .l4cl { --li_w: min(220px, 75vw) }
                       #root .l4cl li { width: var(--li_w) }
             gutter  : .l4cl li { border-left: var(--dist_a) solid transparent }

           The gap is a BORDER on each card, not a flex gap. Adding an
           inline px width and a column-gap on top of that is what made
           the mobile cards wider and the spacing doubled — so we clear
           both and let the theme's own rules stand.
           ------------------------------------------------------------ */
        LIST.style.columnGap = '';
        LIST.style.scrollSnapType = '';
        LIST.style.scrollPaddingLeft = '';
        items.forEach(function(li) {
          li.style.flex = '';
          li.style.width = '';
          li.style.maxWidth = '';
        });
        return;
      }

      // Manual mode — only when "Match New Arrivals card size automatically" is off.
      const gap = readGap(LIST) || 0;
      const per = isDesktop() ? ITEMS_DESKTOP : ITEMS_MOBILE;
      const avail = LIST.clientWidth;
      if (avail > 0) {
        const cardWidth = (avail - gap * (Math.ceil(per) - 1)) / per;
        items.forEach(function(li) {
          li.style.flex = '0 0 auto';
          li.style.width = cardWidth + 'px';
          li.style.maxWidth = 'none';
        });
      }
    }

    /* ---- Prev / next arrow behaviour (desktop, like New Arrivals) ---- */
    function initArrows() {
      if (!PREV || !NEXT || !LIST || !IS_SLIDER) return;

      function stepSize() {
        const items = LIST.children;
        if (items.length >= 2) return items[1].offsetLeft - items[0].offsetLeft;
        return LIST.clientWidth;
      }

      function positionArrows() {
        // Vertically center the arrows on the product image, like New Arrivals
        const img = LIST.querySelector('li img');
        if (!img) return;
        const cRect = CONTAINER.getBoundingClientRect();
        const iRect = img.getBoundingClientRect();
        if (iRect.height > 0) {
          const top = (iRect.top - cRect.top) + iRect.height / 2;
          PREV.style.top = top + 'px';
          NEXT.style.top = top + 'px';
        }
      }

      function update() {
        const overflow = (LIST.scrollWidth - LIST.clientWidth) > 5;
        const show = overflow && isDesktop() && LIST.style.display !== 'none';
        setArrowsHidden(!show);
        if (!show) return;
        setDisabled(PREV, LIST.scrollLeft <= 5);
        setDisabled(NEXT, LIST.scrollLeft >= (LIST.scrollWidth - LIST.clientWidth - 5));
        positionArrows();
      }

      if (!PREV.dataset.bound) {
        PREV.dataset.bound = '1';

        // role="button" spans need click + keyboard wired up manually
        function bindArrow(el, direction) {
          function activate() {
            if (el.classList.contains('is-disabled')) return;
            LIST.scrollBy({ left: direction * stepSize(), behavior: 'smooth' });
          }
          el.addEventListener('click', activate);
          el.addEventListener('keydown', function(e) {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
              e.preventDefault();
              activate();
            }
          });
        }
        bindArrow(PREV, -1);
        bindArrow(NEXT, 1);

        let scrollRaf = null;
        LIST.addEventListener('scroll', function() {
          if (scrollRaf) return;
          scrollRaf = requestAnimationFrame(function() {
            scrollRaf = null;
            update();
          });
        }, { passive: true });
      }

      update();
      // Was: window.jfyArrowUpdate_{{ uid | replace: '-', '_' }} = update;
      // Rebuilt at runtime so the per-section global name is identical.
      window['jfyArrowUpdate_' + String(SECTION_ID).replace(/-/g, '_')] = update;
    }

    function refreshLayout() {
      applyLayout();
      initArrows();
    }

    // Re-measure on resize (card width is in px, so it must be recalculated)
    let resizeTimer = null;
    window.addEventListener('resize', function() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(refreshLayout, 120);
    });

    // Fetch and inject product cards using Section Rendering API
    async function loadProducts() {
      showLoading();

      const handles = await fetchHandles();
      if (!handles.length) {
        showEmpty();
        return;
      }

      // Pre-resolve product IDs so wishlist buttons work (theme wishlist reads data-product-id, not handle)
      const productIds = {};
      try {
        const resolvePromises = handles.map(function(handle) {
          return fetch('/products/' + handle + '.js').then(function(r) {
            return r.ok ? r.json() : null;
          }).then(function(p) {
            if (p && p.id) productIds[handle] = p.id;
          }).catch(function() {});
        });
        await Promise.all(resolvePromises);
      } catch (e) {}

      // Fetch card markup via Section Rendering API for each product (?section_id= returns raw HTML)
      const cardPromises = handles.map(function(handle) {
        return fetch('/products/' + handle + '?section_id=jfy-product-card').then(function(r) {
          return r.ok ? r.text() : null;
        }).then(function(html) {
          if (!html) return null;
          return { handle: handle, html: html };
        }).catch(function() { return null; });
      });

      const results = await Promise.all(cardPromises);
      const validCards = results.filter(function(r) { return r; });

      if (!validCards.length) {
        showEmpty();
        return;
      }

      // Inject cards in order
      LIST.innerHTML = '';
      validCards.forEach(function(card) {
        // Wrap in a temp div to extract the LI element
        const tmp = document.createElement('div');
        tmp.innerHTML = card.html.trim();
        const li = tmp.querySelector('li') || tmp.firstElementChild;
        if (!li) return;

        // Inject the resolved product ID for wishlist compatibility
        const pid = productIds[card.handle];
        if (pid) {
          const wishBtn = li.querySelector('[data-product-id], .wishlist-toggle, .add-to-wishlist');
          if (wishBtn && !wishBtn.getAttribute('data-product-id')) {
            wishBtn.setAttribute('data-product-id', pid);
          }
        }

        LIST.appendChild(li);
      });

      showList();

      // Match the New Arrivals slider layout now that the cards exist
      refreshLayout();

      // Trigger theme hydration for injected content
      setTimeout(function() {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        document.dispatchEvent(new CustomEvent('shopify:section:load', { detail: { sectionId: SECTION_ID } }));
        // Trigger wishlist re-init if the theme exposes it
        if (typeof window.initWishlist === 'function') window.initWishlist();
      }, 100);

      // Stagger extra cart refreshes to catch skeleton-to-real transitions
      [300, 1200].forEach(function(delay) {
        setTimeout(function() { document.dispatchEvent(new CustomEvent('cart:refresh')); }, delay);
      });

      // Re-measure as lazy images load and after theme hydration settles
      LIST.querySelectorAll('img').forEach(function(img) {
        if (!img.complete) img.addEventListener('load', refreshLayout, { once: true });
      });
      [200, 600, 1500, 2500].forEach(function(delay) { setTimeout(refreshLayout, delay); });
    }

    // Kick off (outer boot has already waited for the DOM)
    loadProducts();
  }

  function boot() {
    document.querySelectorAll('[data-jfy-container]').forEach(initJFY);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();